import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { Lock, Search, X, ArrowLeft } from 'lucide-react';
import { ClientDelivery, AppConfig, GuestComment } from '../types';
import { themeStyles } from '../App';
import { ClientDeliveryView } from './ClientDeliveryView';

interface ClientAreaGlobalProps {
  deliveries: ClientDelivery[];
  config: AppConfig;
  onClose: () => void;
  onAddComment?: (deliveryId: string, comment: GuestComment) => Promise<void>;
}

export function ClientAreaGlobal({ deliveries, config, onClose, onAddComment }: ClientAreaGlobalProps) {
  const [passwordInput, setPasswordInput] = useState('');
  const [error, setError] = useState(false);
  const [matchedDelivery, setMatchedDelivery] = useState<ClientDelivery | null>(null);

  const activeTheme = config.theme || 'cosmic-charcoal';

  // Lock body scroll when Client Area is active
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Restore authenticated delivery session if code was previously entered or saved
  useEffect(() => {
    const savedCode = sessionStorage.getItem('lc_global_client_code');
    if (savedCode && deliveries && deliveries.length > 0) {
      const cleanSaved = savedCode.trim().toLowerCase();
      const found = deliveries.find(d => 
        (d.password || '').trim().toLowerCase() === cleanSaved ||
        (d.id || '').trim().toLowerCase() === cleanSaved
      );
      if (found) {
        setMatchedDelivery(found);
      }
    }
  }, [deliveries]);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (!passwordInput.trim()) {
      setError(true);
      return;
    }
    
    // Find delivery by password or ID (case & space insensitive)
    const cleanInput = passwordInput.trim().toLowerCase();
    const found = deliveries.find(d => 
      (d.password || '').trim().toLowerCase() === cleanInput ||
      (d.id || '').trim().toLowerCase() === cleanInput
    );

    if (found) {
      sessionStorage.setItem('lc_global_client_code', cleanInput);
      const url = new URL(window.location.href);
      url.searchParams.set('delivery', found.id);
      window.history.pushState({}, '', url.toString());
      setMatchedDelivery(found);
      setError(false);
    } else {
      setError(true);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('lc_global_client_code');
    const url = new URL(window.location.href);
    url.searchParams.delete('delivery');
    window.history.pushState({}, '', url.toString());
    setMatchedDelivery(null);
    setPasswordInput('');
  };

  if (matchedDelivery) {
    return (
      <div className="fixed inset-0 z-[100] bg-theme-bg text-theme-text overflow-y-auto min-h-screen">
        <ClientDeliveryView 
          delivery={matchedDelivery} 
          config={config} 
          isStandalone={true} 
          onClose={() => {
            handleLogout();
            onClose();
          }} 
          onLogout={() => {
            handleLogout();
          }}
          onAddComment={onAddComment}
        />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[100] bg-theme-bg text-theme-text min-h-screen overflow-y-auto font-sans selection:bg-theme-accent selection:text-theme-bg flex flex-col">
      <style dangerouslySetInnerHTML={{ __html: themeStyles[activeTheme] }} />

      <header className="fixed top-0 left-0 right-0 z-50 bg-theme-bg/95 backdrop-blur-md border-b border-theme-border px-3 py-2.5 sm:px-6 sm:py-4 flex items-center justify-between">
        <button 
          onClick={onClose} 
          className="text-theme-secondary hover:text-theme-text font-mono text-xs uppercase tracking-wider flex items-center gap-1.5 cursor-pointer bg-theme-card/80 px-2.5 py-1.5 sm:px-3 sm:py-2 border border-theme-border hover:border-theme-accent transition-colors rounded"
        >
          <ArrowLeft className="w-3.5 h-3.5 shrink-0" />
          <span className="hidden sm:inline">Retour au site</span>
          <span className="sm:hidden">Retour</span>
        </button>

        {/* Brand logo/title: hidden on small mobile (<640px) to prevent ANY overlap with header buttons */}
        <div className="hidden sm:block absolute left-1/2 -translate-x-1/2 max-w-[160px] md:max-w-xs text-center px-2 pointer-events-none">
          {config.logoUrl ? (
            <img src={config.logoUrl} alt={config.studioName} className="h-6 md:h-8 max-w-full object-contain mx-auto" />
          ) : (
            <span className="text-xs sm:text-sm md:text-base font-serif tracking-wider text-theme-text font-semibold truncate block">
              {config.studioName}
            </span>
          )}
        </div>

        <button
          onClick={onClose}
          aria-label="Fermer l'espace client"
          title="Fermer l'espace client"
          className="p-1.5 sm:p-2 text-theme-secondary hover:text-theme-text hover:bg-theme-card border border-theme-border rounded-full transition-all cursor-pointer flex items-center justify-center bg-theme-bg"
        >
          <X className="w-4 h-4 sm:w-5 sm:h-5" />
        </button>
      </header>

      <main className="flex-1 flex flex-col items-center justify-center p-4 sm:p-6 md:p-12 pt-28 sm:pt-32 w-full max-w-6xl mx-auto">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative w-full max-w-md bg-theme-card border border-theme-border p-6 sm:p-8 md:p-12 text-center rounded-lg shadow-2xl"
        >
          <button 
            onClick={onClose}
            className="absolute top-3 right-3 sm:top-4 sm:right-4 text-theme-secondary hover:text-theme-text p-2 transition-colors cursor-pointer rounded-full hover:bg-theme-bg/50"
            title="Fermer"
            aria-label="Fermer"
          >
            <X className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>

          <Lock className="w-8 h-8 text-theme-accent mx-auto mb-6 opacity-80" />
          <h2 className="text-2xl font-serif text-theme-text mb-2">Espace Client</h2>
          <p className="text-theme-secondary mb-8 text-sm">Entrez votre code d'accès pour découvrir votre galerie et votre film.</p>
          
          <form onSubmit={handleLogin} className="flex flex-col gap-4">
            <input 
              type="password"
              placeholder="Votre code d'accès (ex: LC-84920)"
              value={passwordInput}
              onChange={(e) => {
                setPasswordInput(e.target.value);
                setError(false);
              }}
              className="w-full bg-theme-bg border border-theme-border text-center text-sm px-4 py-4 text-theme-text outline-none focus:border-theme-accent font-mono transition-colors rounded"
            />
            {error && <p className="text-red-500 text-xs font-mono">Code d'accès introuvable</p>}
            <button 
              type="submit"
              className="w-full bg-theme-accent text-theme-bg px-6 py-4 uppercase tracking-[0.2em] text-xs font-semibold hover:opacity-90 transition-opacity flex items-center justify-center gap-2 cursor-pointer rounded"
            >
              <Search className="w-4 h-4" />
              Accéder
            </button>
          </form>
        </motion.div>
      </main>
    </div>
  );
}
